import { UserFullInfo, UserMan, UserWoman } from "@/types/user";
import { addHours, isBefore } from "date-fns";
import { FirebaseError } from "firebase/app";
import { User } from "firebase/auth";
import {
  MONTHS,
  NAVBAR_LINK,
  NEW_USER_HOURS,
  PERSIST_KEYS,
  STORAGE_KEY,
  SUPPORT_WALLETS,
  USER_SEX
} from "./enum";
import { FIREBASE_ERROR_MESSAGES } from "./firebaseAuthErrorMessages";
import { firebaseTimestampToDate } from "./time";

export const hasTwitterProvider = (user: User | null): boolean => {
  if (!user) return false;
  return user.providerData.some(
    (provider) => provider.providerId === "twitter.com"
  );
};

export const getUserEmail = (user: User | null): string | null | undefined => {
  if (!user) return null;
  return (
    user.email || user.providerData.find((provider) => provider.email)?.email
  );
};

export const checkPublicTwitterUrl = (
  user: UserWoman | UserFullInfo | null,
  targetUser: UserWoman | UserFullInfo | null
) => {
  if (!user || !targetUser || targetUser?.isPublicTwitter === undefined)
    return true;
  if (user.uid === targetUser.uid) return true;
  return targetUser.isPublicTwitter;
};

export const isDateValid = (dateString: string | null | undefined): boolean => {
  if (!dateString) return false;

  const currentTime = Date.now();
  const expireTime = new Date(dateString).getTime();
  return expireTime > currentTime;
};

export const formatFileSize = (file: File | null) => {
  if (!file) return "";
  let value = "";
  const defaultSize = 1024 * 1024;
  if (file.size > defaultSize) {
    value = (file.size / (1024 * 1024)).toFixed(2);
    value = `${value} MB`;
  } else {
    value = (file.size / 1024).toFixed(2);
    value = `${value} KB`;
  }
  return value;
};

export const getMonthObj = (currentMonth: string | undefined) => {
  if (!currentMonth) return MONTHS[0];
  const obj = MONTHS.find((month) => month.periodId === currentMonth);
  return obj || MONTHS[0];
};

export const getTransferLink = (wallet: WalletInfo, link: string) => {
  const { name, mobile } = wallet;
  const universalLink = mobile.universal;
  if (!SUPPORT_WALLETS.includes(name)) return universalLink;

  const urlTransfer =
    link && link.includes("ethereum:")
      ? link.replaceAll("ethereum:", "")
      : link;
  const result = `${universalLink}/send/${urlTransfer}`;

  return result?.includes("//send")
    ? result.replaceAll("//send", "/send")
    : result;
};

export const getFirebaseError = (error: any, defaultCode: string) => {
  const code = error instanceof FirebaseError ? error.code : defaultCode;
  return FIREBASE_ERROR_MESSAGES[code as keyof typeof FIREBASE_ERROR_MESSAGES];
};

export const checkIsNewUser = (createdAt: Date | null | string, uid: string) => {
  if (
    localStorage.getItem(`${STORAGE_KEY.SKIP_NEW_ONBOARDING}-${uid}`) === "true"
  )
    return false;
  if (!createdAt || typeof createdAt === "string") return false;
  const endDate = addHours(createdAt, NEW_USER_HOURS);
  return isBefore(new Date(), endDate);
};

export const isValueEmpty = (value: any): boolean => {
  if (value === null || value === undefined) return true;

  if (typeof value === "boolean") return value === false;

  if (typeof value === "string") return value.trim().length === 0;

  if (Array.isArray(value)) return value.length === 0;

  if (typeof value === "object") {
    const keys = Object.keys(value);
    if (keys.length === 0) return true;

    return keys.every((k) => isValueEmpty(value[k]));
  }

  return false;
};

export const checkIsFullInfoProfile = <T extends UserFullInfo | UserWoman>(
  profileOptionKeys: (keyof T)[],
  selectedOptions: Partial<T>,
  requiredFieldsList: string[]
) => {
  if (!selectedOptions) return false;

  const isEmpty = profileOptionKeys.some(
    (key) =>
      requiredFieldsList.includes(key as string) &&
      isValueEmpty(selectedOptions[key])
  );
  return !Boolean(isEmpty);
};

export function getDefaultUserData<T extends { uid: string }>(
  userData: T | null | undefined
): Record<string, any> {
  if (!userData?.uid) {
    return {};
  }

  const localData = localStorage.getItem(
    `${STORAGE_KEY.NEW_PROFILE}-${userData.uid}`
  );

  return {
    ...userData,
    ...(localData ? JSON.parse(localData) : {})
  } as Record<string, any>;
}

export const checkShowBannerClaimPlan = (user: UserMan, finishedFillInfo: boolean) => {
  if (!user?.uid || user?.sex !== USER_SEX.male || !finishedFillInfo) return false;

  const createdAt = firebaseTimestampToDate(user?.createdAt);
  if (!createdAt || typeof createdAt === "string") return false;

  const endDate = addHours(createdAt, NEW_USER_HOURS);
  const verificationCampaignGranted = Boolean(user.verificationCampaignGranted)
  
  return isBefore(new Date(), endDate) && checkShowSuccessClaimBasicPlan(user.uid, verificationCampaignGranted)
}

export const checkShowSuccessClaimBasicPlan = (uid: string, verificationCampaignGranted: boolean) => {
  if (!uid) return false;
  const isClaimed = localStorage.getItem(
    `${STORAGE_KEY.CLAIMED_BASIC_PLAN_FREE}-${uid}`
  );
  return !(isClaimed === "true" && verificationCampaignGranted);
};

export const removeLocalStorageData = (uid: string) => {
  Object.values(STORAGE_KEY).forEach((key) => {
    if (!PERSIST_KEYS.includes(key)) {
      localStorage.removeItem(key);
      localStorage.removeItem(`${key}-${uid}`);
    }
  });
};

export const getPrevRouteUpdateProfileWomen = (
  uid: string,
  createdAt: string | Date | undefined,
  prevRoute?: string
) => {
  const isUserNew = checkIsNewUser(
    firebaseTimestampToDate(createdAt ?? new Date()),
    uid
  );

  const hasPrevRoute = Boolean(prevRoute);
  if (isUserNew && (prevRoute === NAVBAR_LINK.home || !hasPrevRoute))
    return NAVBAR_LINK.home;

  return NAVBAR_LINK.mypage;
};
